import axios from 'axios';
import { setupAxiosInterceptor } from './auth/axiosConfig';
import config from './config';

/**
 * subagentsApi: Subagents v1 (agent_docs/subagents_v1_prd.md section 10; the API as built in
 * agent_docs/subagents/BE_BUILD_SUMMARY.md, py/cellcog/api/subagents.py). Router prefix /api/cellcog/subagents; the standard
 * services/* pattern (an axios instance + the auth interceptor). The app READS; the writes are the owner's Cancel, Check now
 * and the per-tool key setting.
 *
 *   GET  /subagents?status=&page=   -> { subagents: [CARD], page, has_more, readiness: READINESS }  (50 a page, newest first;
 *                                      status = waiting | reported | never_reported | cancelled; a bad status is 400)
 *   GET  /subagents/sections?q=&page= -> { sections: [SECTION], page, has_more, readiness: READINESS } (the page, Oct 14 2026:
 *                                      20 parents a page, newest first; q = a case-insensitive name search across sections)
 *   GET  /subagents/done?parent=&q=&offset=&limit= -> { subagents: [ROW], offset, has_more } (a section's Show more: that
 *                                      parent's done rows after the `offset` already shown, newest first)
 *   POST /subagents/{key}/cancel    -> CARD (the updated card; another user's or org's key is 404; idempotent)
 *   POST /subagents/cli-check       -> { request_id, status: 'pending' }; 409 { detail: { code: desktop_not_connected |
 *                                      desktop_outdated | multiple_desktops, message } } (agent_docs/subagents/CHECK_NOW_CONTRACT.md)
 *   GET  /subagents/cli-check/{id}  -> { status: pending | done | expired, checked_at, hostname, results: { kind: RESULT } | null }
 *   POST /subagents/cli-settings    -> the kind's READINESS entry; body { kind, api_key_fallback }
 *
 *   CARD = { key, name, kind: cellcog|claude_code|codex|cursor|other, status, parent_kind: chat|employee,
 *            parent_post_office_id, parent_label, metadata, urls: [{ issued_at, expires_at, uses, max_uses, expect_by }],
 *            wakes: [{ at, use_n }], last_wake_at, created_at, attention_at, reviewed_at, needs_review }
 *          (+ child_operating on the list: true/false for a CellCog row whose child chat is the owner's, else null)
 *   SECTION = { parent_kind, parent_post_office_id, parent_label, working: [ROW] (every waiting row), done: [ROW] (the newest
 *               5 reported / never reported / cancelled), working_count, done_count }
 *   ROW = CARD made slim: urls is the latest URL only ({ issued_at, expires_at, expect_by }, no use counts), no wakes,
 *         metadata keeps scalar values only; + child_operating
 *   Review (agent_docs/subagents/REVIEW_PRIMITIVE_CONTRACT.md): attention_at / reviewed_at are ISO or null; needs_review is
 *   derived server-side (attention_at set and newer than reviewed_at). Only the parent AGENT clears it (Subagent_Update
 *   reviewed: true); no route here can, so opening the page or the strip never does. The SUBAGENT_UPDATED event carries the
 *   same three fields (op "reviewed" when the agent reviews).
 *   READINESS = { cellcog: { ready, key_name }, claude_code|codex|cursor: { desktop_connected, cli_ok, cli_checked_at,
 *                 cli_version, desktop_check_supported, check: { checked_at, hostname, platform, ...RESULT } | null,
 *                 api_key_fallback, usage: USAGE | null } }
 *   RESULT = { installed, version, subscription: { signed_in, plan, account }, api_key: { set, vars }, key_from_settings, error }
 *   USAGE = { kind, source, captured_at, raw } (agent_docs/subagents/USAGE_READINGS_CONTRACT.md): the newest usage reading for
 *           that tool; `raw` is the tool's own data exactly as given (object, array or string, free-form, at most 8 KB).
 * Scope is the server's: the current user's subagents in the request's organization (machine facts are per user).
 */
const subagentsApi = axios.create({
  baseURL: config.API_BASE_URL + '/cellcog/subagents',
});

setupAxiosInterceptor(subagentsApi);

/** GET ?page&status -> { subagents, page, has_more, readiness }. */
export const listSubagents = ({ page = 1, status } = {}) => {
  const params = { page };
  if (status) params.status = status;
  return subagentsApi.get('', { params });
};

/** GET /sections?page&q -> { sections, page, has_more, readiness }. */
export const listSubagentSections = ({ page = 1, q } = {}) => {
  const params = { page };
  if (q) params.q = q;
  return subagentsApi.get('/sections', { params });
};

/** GET /done?parent&offset&limit&q -> { subagents, offset, has_more }: one section's older done rows. */
export const listSubagentsDone = ({ parent, offset = 0, limit = 20, q } = {}) => {
  const params = { parent, offset, limit };
  if (q) params.q = q;
  return subagentsApi.get('/done', { params });
};

/**
 * GET /for-chat/{chatId} -> { parent_kind, parent_post_office_id, subagents: [CARD + updated_at, child_status, child_operating],
 * window_hours }. The chat page's subagent strip (TRACK): waiting rows plus rows changed in the last 24 h. chatId = a regular
 * chat, or an AI employee's anchor (or shift) id. Another user's chat is 404.
 */
export const listSubagentsForChat = (chatId) => subagentsApi.get(`/for-chat/${encodeURIComponent(String(chatId || ''))}`);

/** POST /{key}/cancel -> the updated card. */
export const cancelSubagent = (key) => subagentsApi.post(`/${encodeURIComponent(String(key || ''))}/cancel`);

/** POST /cli-check -> { request_id, status }. Asks CellCog Desktop to run its built-in check now (one pending request a user). */
export const startCliCheck = () => subagentsApi.post('/cli-check');

/** GET /cli-check/{requestId} -> { status, checked_at, hostname, results }. The setup drawer polls it every 2 s, 45 s at most. */
export const getCliCheck = (requestId) => subagentsApi.get(`/cli-check/${encodeURIComponent(String(requestId || ''))}`);

/** POST /cli-settings { kind, api_key_fallback } -> the kind's readiness entry ("Use my API key when my plan's limit is reached"). */
export const saveCliSettings = (kind, apiKeyFallback) => subagentsApi.post('/cli-settings', {
  kind,
  api_key_fallback: Boolean(apiKeyFallback),
});

/** GET /running -> { parents: { [parent_post_office_id]: count } }: the sidebar's subagent icon (running = waiting takes). */
export const listRunningSubagentParents = () => subagentsApi.get('/running');

export default subagentsApi;
